import { useLoadingState } from '@ds-mgnl/marketing';
import type { AssetDimension, FamilyAssetMap } from '../services/productApi';
import { getDimensionValueAssetMap } from '../services/productApi';

interface ProductMaterialAssetMapState {
	map: FamilyAssetMap | null;
	isLoading: boolean;
	error: string | null;
}

/**
 * Builds a synthetic single-product asset map for a product that has no family
 * asset map, by fetching the per-value asset map (see getDimensionValueAssetMap)
 * for each material dimension the product fixes in its own attributes (e.g.
 * frame finish, nailhead finish — see materialAttributeSelections).
 *
 * The result is shaped like a FamilyAssetMap so it drops straight into the
 * existing buildModelMaterials path alongside the real family-map flow. A
 * failed fetch for one dimension is skipped rather than failing the whole map,
 * so one missing finish never blanks the rest of the model.
 *
 * `selections` MUST be referentially stable across renders for the same product
 * (memoize it in the caller); the fetch re-runs whenever its identity changes.
 */
export function useProductMaterialAssetMap(
	productId: string | undefined,
	selections: Record<string, string>,
): ProductMaterialAssetMapState {
	const { data, pending, rejected } = useLoadingState<FamilyAssetMap | null>(
		async () => {
			const entries = Object.entries(selections);
			if (!productId || entries.length === 0) return null;

			const dimensions: Record<string, AssetDimension> = {};
			const results = await Promise.allSettled(
				entries.map(async ([dimensionSlug, valueSlug]) => {
					const map = await getDimensionValueAssetMap(dimensionSlug, valueSlug);
					// The endpoint keys the dimension by its slug; fall back to the
					// sole returned dimension in case the API keys it differently.
					const dimension =
						map.dimensions[dimensionSlug] ?? Object.values(map.dimensions)[0];
					return [dimensionSlug, dimension] as const;
				}),
			);

			for (const result of results) {
				if (result.status !== 'fulfilled') continue;
				const [dimensionSlug, dimension] = result.value;
				if (dimension) dimensions[dimensionSlug] = dimension;
			}

			if (Object.keys(dimensions).length === 0) return null;

			return {
				id: '',
				name: '',
				variants: {},
				assets: { dimensions },
				products: { [productId]: selections },
			};
		},
		[productId, selections],
	);

	return {
		map: data,
		isLoading: pending,
		error: rejected ? 'Failed to load product material asset map' : null,
	};
}
